import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useProductNameBySlug } from "../components/IronPanelPurchase/productCopy";

// Written by useCheckout right before the Stripe redirect, keyed by product
// slug (not Stripe lookup key -- see productCopy.ts) so this hook can join
// back to i18n copy without trusting a second source of product names.
export const PENDING_CHECKOUT_ITEMS_KEY = "ferrivia:store:pending-checkout-items";

// Stripe's webhook (which flips an order to "paid") is delivered
// asynchronously and can race the browser's own success_url redirect, so a
// short poll is required rather than trusting the first read.
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15; // ~30s budget

export type OrderConfirmationState =
  | { status: "hidden" }
  | { status: "loading"; orderNumber: string }
  | {
      status: "paid";
      orderNumber: string;
      totalCents: number;
      currency: string;
      itemNames: string[];
      /** The product slugs this order was for, so the caller can ask the
          catalog whether any of them carries lab measurements — the dialog's
          booking CTA turns on that. Empty when the stash is gone (a reopened
          tab, cleared session storage), which is why the dialog treats an
          empty list as "we don't know" and falls back to its neutral way out
          rather than promising a laboratory appointment. */
      itemSlugs: string[];
    }
  | { status: "timed_out"; orderNumber: string }
  | { status: "unsuccessful"; orderNumber: string };

export interface UseOrderConfirmationResult {
  state: OrderConfirmationState;
  dismiss: () => void;
}

function readPendingItemSlugs(): string[] {
  const stashed = sessionStorage.getItem(PENDING_CHECKOUT_ITEMS_KEY);
  sessionStorage.removeItem(PENDING_CHECKOUT_ITEMS_KEY);
  if (!stashed) return [];
  try {
    const parsed: unknown = JSON.parse(stashed);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((slug): slug is string => typeof slug === "string");
  } catch {
    return [];
  }
}

export function useOrderConfirmation(): UseOrderConfirmationResult {
  const api = useApi();
  const tracker = useTracker();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const nameBySlug = useProductNameBySlug();

  const orderNumber = searchParams.get("order");
  const token = searchParams.get("token");

  const [state, setState] = useState<OrderConfirmationState>(
    orderNumber && token ? { status: "loading", orderNumber } : { status: "hidden" },
  );

  const viewedTrackedRef = useRef(false);
  const resolvedTrackedRef = useRef(false);

  useEffect(() => {
    if (!orderNumber || !token) {
      setState({ status: "hidden" });
      return;
    }

    if (!viewedTrackedRef.current) {
      viewedTrackedRef.current = true;
      tracker.track("store.order_confirmation_viewed", {});
    }

    const itemSlugs = readPendingItemSlugs();
    const itemNames = itemSlugs.map((slug) => nameBySlug(slug)).filter((name): name is string => Boolean(name));
    setState({ status: "loading", orderNumber });

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const poll = () => {
      api.commerce
        .getOrderStatus(orderNumber, token)
        .then((res) => {
          if (cancelled) return;
          if (res.status === "paid") {
            setState({
              status: "paid",
              orderNumber,
              totalCents: res.totalCents,
              currency: res.currency,
              itemNames,
              itemSlugs,
            });
            return;
          }
          if (res.status === "payment_failed" || res.status === "expired" || res.status === "cancelled") {
            setState({ status: "unsuccessful", orderNumber });
            return;
          }
          scheduleRetry();
        })
        .catch(() => {
          if (cancelled) return;
          scheduleRetry();
        });
    };

    const scheduleRetry = () => {
      attempts += 1;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        setState({ status: "timed_out", orderNumber });
        return;
      }
      timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // nameBySlug is a fresh closure every render (built from t()) and must
    // not retrigger this effect -- only the URL params should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, token, api, tracker]);

  useEffect(() => {
    if (resolvedTrackedRef.current) return;
    if (state.status === "paid" || state.status === "timed_out" || state.status === "unsuccessful") {
      resolvedTrackedRef.current = true;
      tracker.track("store.order_confirmation_resolved", { status: state.status });
    }
  }, [state, tracker]);

  // Stays on the tab Stripe returned to (the Iron infusion tab, whose labs
  // step now reads as in progress). Dropping ?order=&token= is what closes
  // the dialog, so this doubles as the dismissal.
  const dismiss = useCallback(() => {
    navigate(pathname, { replace: true });
  }, [navigate, pathname]);

  return { state, dismiss };
}
